import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ApiService,
  IListData,
  IListParams,
} from 'src/app/shared/services/api/api/api.service';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToasterService } from 'src/app/shared/services/toaster/toaster.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, IUser } from 'src/app/shared/auth/auth.service';
import { AdminRoleTypeEnum } from 'src/app/shared/auth/AdminRoleTypeEnum';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent implements OnInit {
  @ViewChild('customContent') private _customContent;
  users = [];
  currentUser: IUser = null;
  formUsers: UntypedFormGroup = this._formBuilder.group({
    name: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, this.checkPasswordsValidity]],
    roles: ['', [Validators.required]],
  });

  adminRoles = Object.values(AdminRoleTypeEnum);
  submitType = 'Add';
  totalUsers = 0;
  limit = 10;
  skip = 0;
  search = '';
  constructor(
    private _api: ApiService,
    private _formBuilder: UntypedFormBuilder,
    private _cdr: ChangeDetectorRef,
    private _toast: ToasterService,
    private _modalService: NgbModal,
    private _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.getUsers();
    this.currentUser = this._authService.currentUserValue;
  }

  getUsers(): void {
    const query: IListParams = { limit: this.limit, skip: this.skip };

    if (this.search) {
      query.search = this.search;
    }

    this._api.getUsers(query).subscribe(
      (data: IListData) => {
        this.users = data.list;
        this.totalUsers = data.totalCount;
        this._cdr.detectChanges();
      },
      (err) => {
        this._toast.showError(err.error.message);
      },
    );
  }

  addUser(): void {
    this.resetForm();
    this.submitType = 'Add';
    this._modalService.open(this._customContent, {
      windowClass: 'dark-modal',
      backdrop: 'static',
      size: 'lg',
    });
  }

  resetForm(): void {
    this.formUsers.reset();
    this.formUsers.addControl(
      'password',
      new UntypedFormControl('password', [Validators.required]),
    );
    this.formUsers.removeControl('_id');
  }

  checkPasswordsValidity(password: UntypedFormControl): any {
    if (!password) {
      return true;
    }

    const pass = password.value;
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    return passwordRegex.test(pass) ? null : { passwordNotValid: true };
  }

  submitUser(): boolean {
    if (this.submitType === 'Add') {
      this._api.addUser(this.formUsers.value).subscribe(
        (): void => {
          this.getUsers();
          this._toast.showSuccess('User Updated Successfully');
        },
        (err) => {
          this._toast.showError(err.error.message);
        },
      );
    } else if (this.submitType === 'Edit') {
      this._api.editUser(this.formUsers.value).subscribe(
        (): void => {
          this._toast.showSuccess('User edited successfully');
          this.getUsers();
        },
        (err) => {
          this._toast.showError(err.error.message);
        },
      );
    }

    return false;
  }

  editUser(userIndex): void {
    const user = this.users[userIndex];
    this.resetForm();
    this.submitType = 'Edit';
    this.formUsers.removeControl('password');
    this.formUsers.controls.name.patchValue(user.name);
    this.formUsers.controls.email.patchValue(user.email);
    this.formUsers.controls.roles.patchValue(user.roles);
    this.formUsers.addControl('_id', new UntypedFormControl(user._id));
    this._modalService.open(this._customContent, {
      windowClass: 'dark-modal',
      backdrop: 'static',
      size: 'lg',
    });
  }

  deleteUser(user): boolean {
    if (user._id === this.currentUser._id) {
      this._toast.showError('You cannot delete yourself');
      return false;
    }

    this._api.deleteUser(user).subscribe(
      (): void => {
        this._toast.showSuccess('User deleted successfully');
        this.getUsers();
      },
      (err) => {
        this._toast.showError(err.error.message);
      },
    );
  }

  loadPageData(data): void {
    this.skip = data.index;
    this.search = data.search ? data.search : '';
    this.getUsers();
  }

  // TODO --confirm if one super-admin can change the role of another super admin
  roleCheck(): boolean {
    if (
      this.formUsers.get('_id')?.value === this.currentUser._id ||
      this.formUsers.get('roles')?.value?.includes('super-admin')
    ) {
      return true;
    } else {
      return false;
    }
  }

  rolesFilter(): string[] {
    if (!this.currentUser.roles.includes('super-admin')) {
      return this.adminRoles.filter((data) => data !== 'super-admin');
    } else {
      return this.adminRoles;
    }
  }
}
